import { PL_REPORT, DOWNLOAD_FILE_NAME, OPERATION_STATUS, REPORT_KEYS, SNAPSHOT_ID } from './common/constants';
import { COMPONENT_TO_LINK } from '../../constants/urls';

import ComponentLink from '../content/controls/component_link';
import { formatHeadingDisplay, formatCellDisplay, getFormat } from './utils/formatting';
import { zip, isEmpty } from './utils';
import { ReportTable } from './components/report_table';
import { buildContentSelectorOption } from './utils/report_preferences';
import { REPORT_TYPE } from '../../constants/report_type';
import { getCompositeKeys, getForEachCompositeKey } from './utils/composite_keys';
import {
  accuracyDataWithCompositeKeys,
  replaceLiveSnapshotId,
  transformToReportFormat  
} from './utils/transformations';
import { reportDataByColumn } from './utils/download';
import { sortByComponentProductLineChannel } from './utils/sorting';

/**
 * Renders a PL report. Contains methods that are specific
 * to the PL report - such as transforming the data to PL
 * report format.
 */
export class PLReport extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
    };
    this.reportData = null;
    this.contentSelectorOptions = null;
  }

  componentDidUpdate = (prevProps) => {
    if (
      prevProps.downloadStatus !== this.props.downloadStatus &&
      this.props.downloadStatus === OPERATION_STATUS.IN_PROGRESS
    ) {
      this.downloadData();
    }
  };

  downloadData = () => {
    const reportData = {
      ...this.reportData,
      reportRows: this.reportData.reportRows.map((r) => {
        return { ...r, visible: true };
      }),
    };

    let dataToDownload = reportDataByColumn(reportData);

    this.props.downloadFile(dataToDownload.join('\n'), DOWNLOAD_FILE_NAME);
    this.props.onDownloadDataSuccess();
  };

  parsePayloadData = () => {
    const { requestPayloads, responsePayloads, liveSnapshot } = this.props;

    const dataSetKeys = [SNAPSHOT_ID];

    let compositeKeys = getCompositeKeys(replaceLiveSnapshotId(requestPayloads, liveSnapshot), dataSetKeys);
    let accuracyData = accuracyDataWithCompositeKeys(responsePayloads, compositeKeys, PL_REPORT.DEDUPED_COL_KEYS);

    const sortedHeadings = [...new Set(responsePayloads.map((p) => p.sortedHeadings).flat())];
    const sortedKeys = [...new Set(responsePayloads.map((p) => p.sortedKeys).flat())];

    const sortedHeadingsWithCompositeKeys = getForEachCompositeKey(sortedHeadings, compositeKeys);
    const sortedKeysWithCompositeKeys = getForEachCompositeKey(sortedKeys, compositeKeys);

    const transformedData = transformToReportFormat(
      sortedKeysWithCompositeKeys,
      sortedHeadingsWithCompositeKeys,
      accuracyData,
      PL_REPORT.DEDUPED_COL_KEYS
    );

    return { ...transformedData, reportRows: sortByComponentProductLineChannel(transformedData.reportRows) };
  };

  getColumnDefinitions = (reportData) => {
    const { reportKeys, reportHeadings } = reportData;
    const { columnFormats, displayInMillions } = this.props;

    let columns = [];

    for (let [key, heading] of zip(reportKeys, reportHeadings)) {

      let columnEntry = {};
      columnEntry['id'] = key;
      columnEntry['header'] = () => formatHeadingDisplay(heading, getFormat(key, columnFormats));
      columnEntry['minWidth'] = PL_REPORT.WRAP_LINE_COLUMNS.includes(key) ? '200px' : '100px';
      columnEntry['allowLineWrap'] =
        PL_REPORT.WRAP_LINE_COLUMNS.includes(key) || PL_REPORT.WRAP_LINE_COLUMNS.some((c) => key.endsWith(c));
      columnEntry['cell'] = (item) => {
        return key === REPORT_KEYS.COMPONENT ? (
          <ComponentLink componentName={item[key]} mapping={COMPONENT_TO_LINK} level={item.level} />
        ) : (
          formatCellDisplay(item[key], getFormat(key, columnFormats), displayInMillions, item)
        );
      };

      columns.push(columnEntry);
    }

    return columns;
  };

  onToggleMillions = () => {
    this.props.onToggleMillions();
  };

  onContentSelectionChange = (event) => {
    this.props.onContentSelectionChange(event);
  };

  getContentSelectorOptions = (reportKeys, reportHeadings) => {
    const { reportPreferences, columnSets } = this.props;

    return [
      {
        options: reportKeys.map((key, i) =>
          buildContentSelectorOption(key, reportHeadings[i], columnSets, reportPreferences, REPORT_TYPE.PL)
        ),
      },
    ];
  };

  render() {
    const { loading, displayInMillions } = this.props;

    if (isEmpty(this.reportData)) {
      this.reportData = this.parsePayloadData();

      const { reportKeys, reportHeadings } = this.reportData;
      this.contentSelectorOptions = this.getContentSelectorOptions(reportKeys, reportHeadings);
      this.props.onContentSelectorOptionsRegenerate(this.contentSelectorOptions);
    }
    const { reportRows } = this.reportData;

    return (
      <div>
        <ReportTable
          loading={loading}
          displayInMillions={displayInMillions}
          reportRows={reportRows}
          columnDefinitions={this.getColumnDefinitions(this.reportData)}
          contentSelectorOptions={this.contentSelectorOptions}
          onContentSelectionChange={this.onContentSelectionChange}
          onToggleMillions={this.onToggleMillions}
          showTableFilter={true}
          wrapLines={true}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/pl_report.js